/**
 * Ops Dashboard API client
 *
 * All requests go to the ops-api worker (/ops/*).
 * Token is stored in sessionStorage and sent as Authorization: Bearer.
 */

const API_BASE = (import.meta as any).env?.VITE_OPS_API_URL ?? "";

// ─── Auth storage ─────────────────────────────────────────────────────────────

const TOKEN_KEY = "bz_ops_token";
const ROLE_KEY  = "bz_ops_role";

export function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function isLoggedIn(): boolean {
  return !!getToken();
}

export function saveSession(token: string, roleName: string): void {
  sessionStorage.setItem(TOKEN_KEY, token);
  sessionStorage.setItem(ROLE_KEY, roleName);
}

export function clearSession(): void {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(ROLE_KEY);
}

export function getRole(): string {
  return sessionStorage.getItem(ROLE_KEY) ?? "";
}

// ─── HTTP helpers ─────────────────────────────────────────────────────────────

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function opsGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { ...authHeaders(), Accept: "application/json" },
  });
  if (res.status === 401) { clearSession(); throw new Error("Session expired"); }
  if (!res.ok) throw new Error(`GET ${path} → ${res.status}`);
  return res.json() as Promise<T>;
}

async function opsPost<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: body != null ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) { clearSession(); throw new Error("Session expired"); }
  if (!res.ok) throw new Error(`POST ${path} → ${res.status}`);
  return res.json() as Promise<T>;
}

async function opsDelete<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (res.status === 401) { clearSession(); throw new Error("Session expired"); }
  if (!res.ok) throw new Error(`DELETE ${path} → ${res.status}`);
  return res.json() as Promise<T>;
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

export const auth = {
  login: (email: string) =>
    opsPost<{ token: string; roleName: string; permissions: string[] }>("/ops/auth/login", { email }),
  logout: () => opsPost("/ops/auth/logout"),
  me: () => opsGet<{ adminEmail: string; roleName: string; permissions: string[] }>("/ops/auth/me"),
};

// ─── Tenants ──────────────────────────────────────────────────────────────────

export const tenants = {
  list: (params: Record<string, string> = {}) => {
    const qs = new URLSearchParams(params).toString();
    return opsGet<any>(`/ops/tenants${qs ? `?${qs}` : ""}`);
  },
  create: (data: {
    email: string; password: string; ownerName?: string;
    slug: string; businessName: string;
    planKey?: string; modeKey?: string; themeKey?: string;
  }) => opsPost<any>("/ops/tenants", data),
  get: (id: string) => opsGet<any>(`/ops/tenants/${id}`),
  history: (id: string) => opsGet<any>(`/ops/tenants/${id}/history`),
  suspend: (id: string, reason?: string) => opsPost<any>(`/ops/tenants/${id}/suspend`, { reason }),
  reactivate: (id: string) => opsPost<any>(`/ops/tenants/${id}/reactivate`),
  archive: (id: string, reason?: string) => opsPost<any>(`/ops/tenants/${id}/archive`, { reason }),
  impersonate: (id: string) => opsPost<any>(`/ops/tenants/${id}/impersonate`),
  delete: (id: string) => opsDelete<any>(`/ops/tenants/${id}`),
};

// ─── Subscriptions ────────────────────────────────────────────────────────────

export const subscriptions = {
  list: (params: Record<string, string> = {}) => {
    const qs = new URLSearchParams(params).toString();
    return opsGet<any>(`/ops/subscriptions${qs ? `?${qs}` : ""}`);
  },
  get: (tenantId: string) => opsGet<any>(`/ops/subscriptions/${tenantId}`),
  changePlan: (tenantId: string, planKey: string, reason?: string) =>
    opsPost<any>(`/ops/subscriptions/${tenantId}/plan`, { planKey, reason }),
  extendTrial: (tenantId: string, days: number, reason?: string) =>
    opsPost<any>(`/ops/subscriptions/${tenantId}/trial`, { days, reason }),
  resetQuota: (tenantId: string) => opsPost<any>(`/ops/subscriptions/${tenantId}/quota-reset`),
  grantEntitlement: (tenantId: string, data: { type: string; key: string; value: string; reason?: string }) =>
    opsPost<any>(`/ops/subscriptions/${tenantId}/grant`, data),
  revokeEntitlement: (tenantId: string, type: string, key: string) =>
    opsPost<any>(`/ops/subscriptions/${tenantId}/revoke`, { type, key }),
};

// ─── Publishing ───────────────────────────────────────────────────────────────

export const publishing = {
  list: (params: Record<string, string> = {}) => {
    const qs = new URLSearchParams(params).toString();
    return opsGet<any>(`/ops/publishing${qs ? `?${qs}` : ""}`);
  },
  get: (tenantId: string) => opsGet<any>(`/ops/publishing/${tenantId}`),
  republish: (tenantId: string) => opsPost<any>(`/ops/publishing/${tenantId}/republish`),
  invalidate: (tenantId: string) => opsPost<any>(`/ops/publishing/${tenantId}/invalidate`),
  rebuild: (tenantId: string) => opsPost<any>(`/ops/publishing/${tenantId}/rebuild`),
  unpublish: (tenantId: string) => opsPost<any>(`/ops/publishing/${tenantId}/unpublish`),
};

// ─── Workers ──────────────────────────────────────────────────────────────────

export const workers = {
  all: () => opsGet<any>("/ops/workers"),
  get: (name: string) => opsGet<any>(`/ops/workers/${name}`),
};

// ─── Metrics ─────────────────────────────────────────────────────────────────

export const metrics = {
  overview: () => opsGet<any>("/ops/metrics"),
  tenants: () => opsGet<any>("/ops/metrics/tenants"),
  ai: () => opsGet<any>("/ops/metrics/ai"),
  publishing: () => opsGet<any>("/ops/metrics/publishing"),
};

// ─── Search ───────────────────────────────────────────────────────────────────

export const search = {
  query: (q: string, types?: string) => {
    const qs = new URLSearchParams({ q, ...(types ? { types } : {}) }).toString();
    return opsGet<any>(`/ops/search?${qs}`);
  },
};

// ─── Audit Log ────────────────────────────────────────────────────────────────

export const auditLog = {
  list: (params: Record<string, string> = {}) => {
    const qs = new URLSearchParams(params).toString();
    return opsGet<any>(`/ops/audit-log${qs ? `?${qs}` : ""}`);
  },
  get: (id: string) => opsGet<any>(`/ops/audit-log/${id}`),
};

// ─── Providers ────────────────────────────────────────────────────────────────

export const providers = {
  list: () => opsGet<any>("/ops/providers"),
  health: (type: string) => opsGet<any>(`/ops/providers/${type}/health`),
  setPrimary: (type: string) => opsPost<any>("/ops/providers/primary", { type }),
  setFallback: (type: string | null) => opsPost<any>("/ops/providers/fallback", { type }),
  metrics: () => opsGet<any>("/ops/providers/metrics"),
};

// ─── Notifications ────────────────────────────────────────────────────────────

export const notifications = {
  list: (params: Record<string, string> = {}) => {
    const qs = new URLSearchParams(params).toString();
    return opsGet<any>(`/ops/notifications${qs ? `?${qs}` : ""}`);
  },
  unreadCount: () => opsGet<{ count: number }>("/ops/notifications/unread-count"),
  markRead: (id: string) => opsPost<any>(`/ops/notifications/${id}/read`),
  dismiss: (id: string) => opsPost<any>(`/ops/notifications/${id}/dismiss`),
};
